// All subscription tiers information (Unified Architecture)
// No more separation between personal and organization tiers

export type Tier = 'free' | 'standard' | 'pro' | 'team' | 'club' | 'pro_team';

export interface TierInfo {
  id: string;
  name: string;
  price: number; // Monthly price in rubles
  priceYearly?: number; // Yearly price in rubles (17% discount)
  topFeatures: string[]; // Top 3-4 features for compact card display
  features: string[]; // Full list of features
  maxMembers: number; // 1 = solo, >1 = team
  trialDays: number; // Trial period duration (0 = no trial)
  highlight?: boolean;
}

// All tiers (no separation)
// Trial periods: STANDARD/TEAM → 7 days, PRO/CLUB/PRO_TEAM → 14 days
export const ALL_TIERS: Record<Tier, TierInfo> = {
  free: {
    id: 'free',
    name: 'FREE',
    price: 0,
    maxMembers: 1,
    trialDays: 0,
    topFeatures: [
      '1 направление',
      '15 спортсменов',
      '1 группа',
      '10 AI-запросов'
    ],
    features: [
      '1 участник',
      '1 направление',
      'До 15 спортсменов',
      '1 группа',
      'До 2 тренеров',
      '10 AI-запросов/мес'
    ]
  },
  standard: {
    id: 'standard',
    name: 'STANDARD',
    price: 699,
    priceYearly: 6990,
    maxMembers: 1,
    trialDays: 7,
    topFeatures: [
      '3 направления',
      '50 спортсменов',
      '5 групп',
      '150 AI-запросов'
    ],
    features: [
      '1 участник',
      'До 3 направлений',
      'До 50 спортсменов',
      'До 5 групп',
      'До 10 тренеров',
      '150 AI-запросов/мес',
      '7 дней бесплатного триала'
    ]
  },
  pro: {
    id: 'pro',
    name: 'PRO',
    price: 1490,
    priceYearly: 14900,
    maxMembers: 1,
    trialDays: 14,
    topFeatures: [
      '10 направлений',
      '200 спортсменов',
      '20 групп',
      '500 AI-запросов'
    ],
    features: [
      '1 участник',
      'До 10 направлений',
      'До 200 спортсменов',
      'До 20 групп',
      'До 30 тренеров',
      '500 AI-запросов/мес',
      '14 дней бесплатного триала'
    ],
    highlight: true
  },
  team: {
    id: 'team',
    name: 'TEAM',
    price: 1990,
    priceYearly: 19900,
    maxMembers: 3,
    trialDays: 7,
    topFeatures: [
      '3 участника',
      '100 спортсменов',
      '20 групп',
      '500 AI-запросов'
    ],
    features: [
      'До 3 участников',
      'До 10 направлений',
      'До 100 спортсменов',
      'До 20 групп',
      'До 10 тренеров',
      '500 AI-запросов/мес',
      '7 дней бесплатного триала'
    ]
  },
  club: {
    id: 'club',
    name: 'CLUB',
    price: 4990,
    priceYearly: 49900,
    maxMembers: 10,
    trialDays: 14,
    topFeatures: [
      '10 участников',
      '500 спортсменов',
      '50 групп',
      '1000 AI-запросов'
    ],
    features: [
      'До 10 участников',
      'До 30 направлений',
      'До 500 спортсменов',
      'До 50 групп',
      'До 30 тренеров',
      '1 000 AI-запросов/мес',
      '14 дней бесплатного триала'
    ],
    highlight: true
  },
  pro_team: {
    id: 'pro_team',
    name: 'PRO TEAM',
    price: 9990,
    priceYearly: 99900,
    maxMembers: 30,
    trialDays: 14,
    topFeatures: [
      '30 участников',
      '2000 спортсменов',
      '200 групп',
      '5000 AI-запросов'
    ],
    features: [
      'До 30 участников',
      'До 100 направлений',
      'До 2 000 спортсменов',
      'До 200 групп',
      'До 100 тренеров',
      '5 000 AI-запросов/мес',
      '14 дней бесплатного триала'
    ]
  }
};

// Helper to get all tiers as array
export function getAllTiers(): TierInfo[] {
  return Object.values(ALL_TIERS);
}

// Helper to get tier by id
export function getTierById(id: string): TierInfo | undefined {
  return ALL_TIERS[id as Tier];
}

// Helper to format price
export function formatPrice(price: number): string {
  if (price === 0) return 'Бесплатно';
  return `${price.toLocaleString('ru-RU')} ₽/мес`;
}

// Helper to format yearly price
export function formatYearlyPrice(price: number): string {
  if (price === 0) return '';
  return `${price.toLocaleString('ru-RU')} ₽/год`;
}

// Helper to check if tier is for teams
export function isTeamTier(tier: TierInfo): boolean {
  return tier.maxMembers > 1 || tier.maxMembers === -1;
}

// Helper to format trial period
export function formatTrialPeriod(days: number): string {
  if (days === 0) return '';
  return `${days} ${days === 7 ? 'дней' : 'дней'} бесплатного триала`;
}
